<template>
  <div class="editar-miembro-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="editar-miembro-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div>
          <h1 class="page-title">Editar miembro</h1>
          <p class="page-sub">Revisa con atención todos los campos</p>
        </div>

        <button class="btn-ghost" @click="$router.push('/miembros')">
          <v-icon size="15" class="mr-2">mdi-arrow-left</v-icon>
          Cancelar
        </button>
      </header>

      <!-- Form Content -->
      <section class="fade-in delay-100">
        <form-miembro :miembro="datosMiembro" @registrado="onEditar" />
      </section>
    </div>

    <!-- Loading Overlay -->
    <div v-if="cargando" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p class="loading-text">{{ cargandoTexto }}</p>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="mostrarMensaje" :timeout="3000" :color="mensaje.color" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import FormMiembro from './FormMiembro.vue'

export default {
  name: "EditarMiembro",
  components: { FormMiembro },

  data: () => ({
    cargando: false,
    cargandoTexto: 'Cargando información...',
    datosMiembro: {
      datosPersonales: null,
      datosContacto: null
    },
    // Guardamos la ruta de la imagen tal cual viene de la base de datos
    // (antes de que se convierta en URL completa para mostrarse en
    // pantalla). Si el usuario no cambia la foto, reutilizamos este valor
    // directamente en vez de intentar "revertir" la URL de despliegue.
    imagenOriginal: null,
    mostrarMensaje: false,
    mensaje: {
      texto: "",
      color: ""
    },
    // Bandera de protección: si por cualquier motivo cargarDatosMiembro()
    // llega a dispararse dos veces (visto solo al abrir un enlace directo
    // a esta ruta en una pestaña nueva del navegador — un doble montaje
    // en el arranque en frío de la SPA), la segunda llamada se ignora en
    // vez de volver a pedir los datos y sobreescribir lo que el usuario
    // ya escribió en el formulario.
    yaCargado: false
  }),

  computed: {
    mensajeIcon() {
      switch (this.mensaje.color) {
        case 'success': return 'mdi-check-circle';
        case 'error': return 'mdi-alert-circle';
        case 'warning': return 'mdi-alert';
        default: return 'mdi-information';
      }
    }
  },

  mounted() {
    this.cargarDatosMiembro();
  },

  methods: {
    async cargarDatosMiembro() {
      if (this.yaCargado) return;
      this.yaCargado = true;

      this.cargando = true;
      this.cargandoTexto = 'Cargando información del miembro...';

      const payload = {
        metodo: "obtener_id",
        id: this.$route.params.id
      };

      try {
        const resultado = await HttpService.obtenerConDatos(payload, "miembros.php");

        this.datosMiembro.datosPersonales = {
          nombre: resultado.nombre,
          telefono: resultado.telefono,
          direccion: resultado.direccion,
          edad: resultado.edad,
        };

        this.datosMiembro.datosContacto = {
          sufreEnfermedad: resultado.sufreEnfermedad,
          tieneSeguro: resultado.tieneSeguro,
          enfermedad: resultado.enfermedad,
          institucion: resultado.institucion,
          afiliacion: resultado.afiliacion,
          nombreContacto: resultado.nombreContacto,
          telefonoContacto: resultado.telefonoContacto,
        };

        this.datosMiembro.imagen = resultado.imagen;
        this.imagenOriginal = resultado.imagen;
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "Error al cargar la información del miembro",
          color: "error"
        };

        // Redirigir después de un error
        setTimeout(() => {
          this.$router.push({ name: "Miembros" });
        }, 2000);
      } finally {
        this.cargando = false;
      }
    },

    async onEditar(miembro) {
      const imagenCambia = miembro.imagen.includes('data:image');

      if (!imagenCambia) {
        // No se cambió la foto: usamos la ruta original guardada al
        // cargar el miembro, sin intentar revertir la URL de despliegue
        // (eso era lo que fallaba antes con .split('api/')).
        miembro.imagen = this.imagenOriginal;
      }

      miembro.imagenCambia = imagenCambia;
      miembro.id = this.$route.params.id;

      this.cargando = true;
      this.cargandoTexto = 'Actualizando información...';

      let payload = {
        metodo: "editar",
        miembro: miembro
      };

      try {
        const resultado = await HttpService.registrar(payload, "miembros.php");

        if (resultado) {
          this.mostrarMensaje = true;
          this.mensaje = {
            texto: "✅ Información de miembro actualizada correctamente",
            color: "success"
          };

          // Redirigir después de éxito
          setTimeout(() => {
            this.$router.push({ name: "Miembros" });
          }, 1500);
        }
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          texto: "❌ Error al actualizar la información",
          color: "error"
        };
      } finally {
        this.cargando = false;
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.editar-miembro-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.editar-miembro-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.editar-miembro-shell {
  position: relative;
  z-index: 1;
  max-width: 860px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
  flex-shrink: 0;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

/* ===== LOADING OVERLAY ===== */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 14, 16, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .spinner { animation: none; }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .editar-miembro-page {
    padding: 20px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Miembros/EditarMiembro.vue